import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignQuoteTemplateItemModel, CampaignQuoteTemplateItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = [
  'campaignQuoteTemplateItems',
  'campaignQuoteTemplateSections',
  'campaignQuoteTemplates',
]

type CampaignQuoteTemplateItemFilters = {
  campaign_quote_template_id?: number,
  campaign_quote_template_section_id?: number,
  string?: string,
}

type UseCampaignQuoteTemplateItemsOptions = {
  filters?: CampaignQuoteTemplateItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteTemplateItemRequestOptions,
}

function useCampaignQuoteTemplateItems(options: UseCampaignQuoteTemplateItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_quote_template_id: filterCampaignQuoteTemplateId,
    campaign_quote_template_section_id: filterCampaignQuoteTemplateSectionId,
    string: filterString,
  } = filters

  const {
    updatedEntities: {
      campaignQuoteTemplateItems: campaignQuoteTemplateItemsUpdatedAt,
      campaignQuoteTemplateSections: campaignQuoteTemplateSectionsUpdatedAt,
      campaignQuoteTemplates: campaignQuoteTemplatesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignQuoteTemplateItems } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignQuoteTemplateItems = useMemo(() => {
    const filtered = Object.values(campaignQuoteTemplateItems).filter((
      campaignQuoteTemplateItem: CampaignQuoteTemplateItemModel,
    ) => {
      const {
        campaign_quote_template_id,
        campaign_quote_template_section_id,
        id,
        title,
      } = campaignQuoteTemplateItem

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchCampaignQuoteTemplateId = matchFilterNumber(campaign_quote_template_id, filterCampaignQuoteTemplateId)
      const matchCampaignQuoteTemplateSectionId = matchFilterNumber(
        campaign_quote_template_section_id,
        filterCampaignQuoteTemplateSectionId,
      )

      return matchCampaignQuoteTemplateId && matchCampaignQuoteTemplateSectionId && matchString
    })

    return sortArrayBy(filtered, 'asc', 'sort')
  }, [
    campaignQuoteTemplatesUpdatedAt,
    campaignQuoteTemplateSectionsUpdatedAt,
    campaignQuoteTemplateItemsUpdatedAt,
    JSON.stringify(filters)])

  const filteredCampaignQuoteTemplateItemsCount = filteredCampaignQuoteTemplateItems.length
  const hasFilteredCampaignQuoteTemplateItems = !!filteredCampaignQuoteTemplateItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignQuoteTemplateItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignQuoteTemplateItems } = useReduxAction(
    'campaignQuoteTemplateItems',
    'loadCampaignQuoteTemplateItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignQuoteTemplateItems,
    filteredCampaignQuoteTemplateItemsCount,
    hasFilteredCampaignQuoteTemplateItems,
    loading: loadingCampaignQuoteTemplateItems,
  }
}

export default useCampaignQuoteTemplateItems
